import {
  Session,
  SupabaseClient,
  User as SupabaseUser,
} from '@supabase/supabase-js';

import { supabase } from '../../config/supabase';
import {
  AuthError,
  AuthSession,
  EmailCredentials,
  EmailSignupData,
  OnboardingProgress,
  PasswordResetData,
  PasswordUpdateData,
  User,
  UserPreferences,
} from '../../types';

import { EmailAuthService } from './core/emailAuth';
import { OAuthService } from './core/oauthService';
import { OnboardingService } from './core/onboardingService';
import { ProfileService } from './core/profileService';
import { SessionManager } from './core/sessionManager';
import {
  NoOpSessionAdapter,
  SessionStorageAdapter,
} from './types/sessionAdapter';

/**
 * Platform-agnostic authentication service
 * Orchestrates all auth-related functionality through specialized services
 */
export class AuthService {
  private supabase: SupabaseClient;
  private sessionManager: SessionManager;
  private emailAuth: EmailAuthService;
  private oauthService: OAuthService;
  private onboardingService: OnboardingService;
  private profileService: ProfileService;

  constructor(
    supabaseClient?: SupabaseClient,
    sessionAdapter?: SessionStorageAdapter
  ) {
    this.supabase = supabaseClient || supabase;
    const adapter = sessionAdapter || new NoOpSessionAdapter();

    // Initialize specialized services
    this.sessionManager = new SessionManager(this.supabase, adapter);
    this.emailAuth = new EmailAuthService(this.supabase);
    this.oauthService = new OAuthService(this.supabase);
    this.onboardingService = new OnboardingService(this.supabase);
    this.profileService = new ProfileService(this.supabase);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Convert Supabase User to custom User type
   */
  private convertSupabaseUser(supabaseUser: SupabaseUser): User {
    return {
      id: supabaseUser.id,
      email: supabaseUser.email || '',
      provider:
        (supabaseUser.app_metadata?.provider as 'google' | 'apple' | 'email') ||
        'email',
      providerId: supabaseUser.app_metadata?.provider_id || supabaseUser.id,
      createdAt: supabaseUser.created_at,
      updatedAt: supabaseUser.updated_at || supabaseUser.created_at,
    };
  }

  /**
   * Convert Supabase Session to custom AuthSession type
   */
  private convertSupabaseSession(supabaseSession: Session): AuthSession {
    return {
      user: this.convertSupabaseUser(supabaseSession.user),
      accessToken: supabaseSession.access_token,
      refreshToken: supabaseSession.refresh_token,
      expiresAt: supabaseSession.expires_at || 0,
    };
  }

  // ============================================================================
  // Session Management (delegates to SessionManager)
  // ============================================================================

  async getCurrentSession(): Promise<AuthSession | null> {
    return this.sessionManager.getCurrentSession();
  }

  async getCurrentUser(): Promise<User | null> {
    return this.sessionManager.getCurrentUser();
  }

  async isAuthenticated(): Promise<boolean> {
    return this.sessionManager.isAuthenticated();
  }

  onSessionChange(
    callback: (session: AuthSession | null, event?: string) => void
  ): () => void {
    return this.sessionManager.onSessionChange(callback);
  }

  async refreshSession(): Promise<{
    success: boolean;
    session?: AuthSession;
    error?: AuthError;
  }> {
    return this.sessionManager.refreshSession();
  }

  async signOut(): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    return this.sessionManager.signOut();
  }

  // ============================================================================
  // Email Authentication (delegates to EmailAuthService)
  // ============================================================================

  async signUpWithEmail(signupData: EmailSignupData): Promise<{
    success: boolean;
    needsVerification: boolean;
    user?: User;
    hasGoals?: boolean;
    error?: AuthError;
  }> {
    const result = await this.emailAuth.signUpWithEmail(signupData);

    // Check if user has goals after signup
    if (result.success && result.user) {
      const hasGoals = await this.profileService.checkUserHasGoals(
        result.user.id
      );
      return {
        ...result,
        user: this.convertSupabaseUser(result.user),
        hasGoals,
      };
    }

    return result.user
      ? { ...result, user: this.convertSupabaseUser(result.user) }
      : { ...result, user: undefined };
  }

  async signInWithEmail(credentials: EmailCredentials): Promise<{
    success: boolean;
    user?: User;
    session?: AuthSession;
    error?: AuthError;
  }> {
    const result = await this.emailAuth.signInWithEmail(credentials);
    return result.user
      ? {
          ...result,
          user: this.convertSupabaseUser(result.user),
          session: result.session
            ? this.convertSupabaseSession(result.session)
            : undefined,
        }
      : {
          ...result,
          user: undefined,
          session: result.session
            ? this.convertSupabaseSession(result.session)
            : undefined,
        };
  }

  async requestPasswordReset(
    resetData: PasswordResetData,
    redirectTo?: string
  ): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    const baseUrl = redirectTo || this.getBaseUrl();
    return this.emailAuth.requestPasswordReset(resetData, baseUrl);
  }

  async updatePassword(passwordData: PasswordUpdateData): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    return this.emailAuth.updatePassword(passwordData);
  }

  async resendEmailVerification(email?: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    return this.emailAuth.resendEmailVerification(email);
  }

  async isEmailVerified(): Promise<boolean> {
    return this.emailAuth.isEmailVerified();
  }

  async checkEmailVerificationStatus(): Promise<{
    isVerified: boolean;
    hasGoals?: boolean;
    shouldRedirectToOnboarding?: boolean;
    error?: AuthError;
  }> {
    return this.emailAuth.checkEmailVerificationStatus(userId =>
      this.profileService.checkUserHasGoals(userId)
    );
  }

  // ============================================================================
  // OAuth Authentication (delegates to OAuthService)
  // ============================================================================

  async signInWithGoogle(redirectTo?: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    const baseUrl = redirectTo || this.getBaseUrl();
    return this.oauthService.signInWithGoogle(baseUrl);
  }

  async signInWithApple(redirectTo?: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    const baseUrl = redirectTo || this.getBaseUrl();
    return this.oauthService.signInWithApple(baseUrl);
  }

  async signInWithCredentials(credentials: {
    provider: 'google' | 'apple';
    idToken: string;
    accessToken?: string;
  }): Promise<{
    success: boolean;
    user?: User;
    session?: AuthSession;
    error?: AuthError;
  }> {
    const result = await this.oauthService.signInWithCredentials(credentials);
    return result.user
      ? {
          ...result,
          user: this.convertSupabaseUser(result.user),
          session: result.session
            ? this.convertSupabaseSession(result.session)
            : undefined,
        }
      : {
          ...result,
          user: undefined,
          session: result.session
            ? this.convertSupabaseSession(result.session)
            : undefined,
        };
  }

  // ============================================================================
  // Onboarding Progress (delegates to OnboardingService)
  // ============================================================================

  async getOnboardingProgress(userId?: string): Promise<{
    success: boolean;
    progress?: OnboardingProgress;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        success: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.getOnboardingProgress(targetUserId);
  }

  async updateOnboardingStep(
    step: 'personal_info' | 'risk_assessment' | 'first_goal' | 'first_account',
    completed: boolean = true,
    userId?: string
  ): Promise<{
    success: boolean;
    progress?: OnboardingProgress;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        success: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.updateOnboardingStep(
      targetUserId,
      step,
      completed,
      async (userId, updates) => {
        await this.profileService.updateUserProfile(userId, updates);
      }
    );
  }

  async completeOnboarding(userId?: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        success: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.completeOnboarding(targetUserId);
  }

  async resetOnboardingProgress(userId?: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        success: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.resetOnboardingProgress(targetUserId);
  }

  async validateOnboardingStep(
    step: 'personal_info' | 'risk_assessment' | 'first_goal' | 'first_account',
    userId?: string
  ): Promise<{
    isValid: boolean;
    canProceed: boolean;
    missingRequirements?: string[];
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        isValid: false,
        canProceed: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.validateOnboardingStep(targetUserId, step);
  }

  async getNextOnboardingStep(userId?: string): Promise<{
    success: boolean;
    nextStep?: string;
    isComplete?: boolean;
    progress?: OnboardingProgress;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        success: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.getNextOnboardingStep(targetUserId);
  }

  async canResumeOnboarding(userId?: string): Promise<{
    canResume: boolean;
    resumeStep?: string;
    completionPercentage?: number;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        canResume: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.onboardingService.canResumeOnboarding(targetUserId);
  }

  async isOnboardingComplete(userId?: string): Promise<boolean> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      console.log('❌ isOnboardingComplete: No user found');
      return false;
    }

    return this.onboardingService.isOnboardingComplete(targetUserId, userId =>
      this.profileService.checkUserHasGoals(userId)
    );
  }

  async isCoreOnboardingComplete(userId?: string): Promise<boolean> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      console.log('❌ isCoreOnboardingComplete: No user found');
      return false;
    }

    return this.onboardingService.isCoreOnboardingComplete(targetUserId);
  }

  // ============================================================================
  // Profile Management (delegates to ProfileService)
  // ============================================================================

  async getUserProfiles(): Promise<{
    profiles: UserPreferences[];
    error?: AuthError;
  }> {
    const user = await this.getCurrentUser();
    if (!user) {
      return {
        profiles: [],
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.profileService.getUserProfiles(user.id);
  }

  async updateUserProfile(
    updates: Partial<{
      first_name: string;
      last_name: string;
      display_name: string;
      avatar_url: string;
      age_group: string;
      income_range: string;
      employment_status: string;
      dependents: number;
      investment_experience: string;
      risk_tolerance: string;
      investment_timeline_years: number;
      status: string;
      onboarding_completed_at: string;
    }>,
    userId?: string
  ): Promise<{
    success: boolean;
    profile?: UserPreferences;
    error?: AuthError;
  }> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return {
        success: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.profileService.updateUserProfile(targetUserId, updates);
  }

  async checkUserHasGoals(userId?: string): Promise<boolean> {
    const targetUserId = userId || (await this.getCurrentUser())?.id;
    if (!targetUserId) {
      return false;
    }

    return this.profileService.checkUserHasGoals(targetUserId);
  }

  async hasGoals(): Promise<{
    hasGoals: boolean;
    error?: AuthError;
  }> {
    const user = await this.getCurrentUser();
    if (!user) {
      return {
        hasGoals: false,
        error: {
          code: 'NOT_AUTHENTICATED',
          message: 'User not authenticated',
        },
      };
    }

    return this.profileService.hasGoals(user.id);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private getBaseUrl(): string {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return '';
  }
}
